import _extends from '@babel/runtime/helpers/esm/extends';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import React, { forwardRef } from 'react';
import { useTheme, Input, FormControl, Stack, FormLabel, Tooltip, FormHelperText, FormErrorMessage, Box, Textarea } from '@chakra-ui/react';
import { getTextStyles, Text } from '@outdoorly/text';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import propTypes from 'prop-types';
import { Cluster } from '@outdoorly/cluster';
import { HelpCircleIcon } from '@outdoorly/icons';

var TextInput = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$size;
  var theme = useTheme();
  var inputStyles = getTextStyles({
    theme: theme,
    size: (_props$size = props.size) !== null && _props$size !== void 0 ? _props$size : 'lg'
  });
  delete inputStyles.padding;
  return /*#__PURE__*/React.createElement(Input, _extends({
    ref: ref
  }, props, {
    sx: _objectSpread({}, inputStyles)
  }));
});

// This is used by Chakra's <InputGroup> component
TextInput.id = 'Input';
if (process.env.NODE_ENV !== 'production') {
  TextInput.displayName = 'TextInput';
}

var _excluded = ["label", "description", "error", "id", "isDisabled", "isRequired", "tooltip", "children"];

/**
 * All other props are spread onto the underlaying [Chakra UI `<Input>`
 * component](https://next.chakra-ui.com/docs/form/input)
 */
var Field = function Field(_ref) {
  var label = _ref.label,
    description = _ref.description,
    error = _ref.error,
    id = _ref.id,
    isDisabled = _ref.isDisabled,
    isRequired = _ref.isRequired,
    tooltip = _ref.tooltip,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  var renderedLabel = /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500",
    as: "span"
  }, label, isRequired && /*#__PURE__*/React.createElement(Box, {
    as: "span",
    color: "scheme.controlActive"
  }, "*"));
  return /*#__PURE__*/React.createElement(FormControl, _extends({
    id: id,
    isInvalid: !!error,
    isDisabled: isDisabled
  }, props), /*#__PURE__*/React.createElement(Stack, {
    spacing: 1
  }, label && /*#__PURE__*/React.createElement(FormLabel, null, tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    label: /*#__PURE__*/React.createElement(Text, {
      color: "scheme.textPrimaryInverse"
    }, tooltip),
    bg: "scheme.bgPrimaryInverse",
    "aria-label": "About this input",
    placement: "top-start",
    hasArrow: true,
    shouldWrapChildren: true,
    px: 4,
    py: 4,
    borderRadius: "lg"
  }, /*#__PURE__*/React.createElement(Cluster, {
    align: "center",
    spacing: 2,
    display: "inline-flex"
  }, renderedLabel, /*#__PURE__*/React.createElement(HelpCircleIcon, {
    color: "scheme.textSecondary",
    _hover: {
      color: 'scheme.controlHover'
    },
    size: "0.8em"
  }))) : renderedLabel), /*#__PURE__*/React.createElement(Stack, {
    spacing: 2
  }, description && /*#__PURE__*/React.createElement(FormHelperText, null, /*#__PURE__*/React.createElement(Text, {
    size: "sm",
    color: "scheme.textSecondary"
  }, description)), children, error && typeof error !== 'boolean' && /*#__PURE__*/React.createElement(FormErrorMessage, null, /*#__PURE__*/React.createElement(Text, {
    size: "sm",
    color: "scheme.textError"
  }, error)))));
};
if (process.env.NODE_ENV !== 'production') {
  Field.displayName = 'Field';
}
Field.propTypes = {
  label: propTypes.node,
  description: propTypes.string,
  /**
   * Show a ﹖ icon with the tooltip string. Requires a `label`
   */
  tooltip: propTypes.string,
  error: propTypes.string,
  id: propTypes.string,
  isDisabled: propTypes.bool,
  isRequired: propTypes.bool,
  children: propTypes.node
};

var TextareaInput = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$size;
  var theme = useTheme();
  var inputStyles = getTextStyles({
    theme: theme,
    size: (_props$size = props.size) !== null && _props$size !== void 0 ? _props$size : 'lg'
  });
  delete inputStyles.padding;
  return /*#__PURE__*/React.createElement(Textarea, _extends({
    ref: ref
  }, props, {
    sx: _objectSpread({}, inputStyles)
  }));
});

// This is used by Chakra's <InputGroup> component
TextareaInput.id = 'Textarea';
if (process.env.NODE_ENV !== 'production') {
  TextareaInput.displayName = 'TextareaInput';
}

export { Field, TextInput, TextareaInput };
